// Client approvals: what the client has approved so far, what the budget has moved since, and how much of it the
// client's purchase orders cover. Pure, so tests/unit/approvals.test.mjs can check it.
//   Issued documents (the estimates table, newest first from estimates.list): full estimates, each with every line
//     as sent ({ itemId, secId, sec, desc, amt }), and change orders (kind 'change'), each with only its changes
//     ({ itemId, secId, sec, desc, was, now }: was null for a new line, now null for a removed one).
//   project.approvals: [{ id, estimateId, date, by, note, fileKey, fileName, recordedBy, recordedAt }], one per
//     approved document, kept on the project so it merges, backs up and goes back in time with the rest.
//   project.pos: [{ id, number, amount, ccy, date, from, covers, note, fileKey, fileName, addedBy }].
// What is approved: the latest approved estimate, with every change order approved after it applied on top.
// Amounts are before tax, in the billing currency, as on the estimate.
import { secTotals } from './budget.js';
import { sentenceCase } from './format.js';

export const r2 = n => Math.round((Number(n) || 0) * 100) / 100;
const key = l => l.itemId || l.desc;
const sum = lines => lines.reduce((s, l) => r2(s + (l.amt || 0)), 0);

// The lines a client estimate prints now: every line not cancelled, at its client price (rounded as on the PDF).
export function clientLines(sections, fx) {
  const out = [];
  (sections || []).forEach(sec => secTotals(sec.items || [], fx).items.filter(ci => ci.st !== 'Cancelled').forEach(ci =>
    out.push({ secId: sec.id, sec: sentenceCase(sec.name), itemId: ci.id, desc: ci.desc, amt: r2(ci.outUSD) })));
  return out;
}

// What changed between two sets of lines: changed amounts or wording, added and removed lines.
export function diffLines(base, now) {
  const bm = new Map((base || []).map(l => [key(l), l])), nm = new Map((now || []).map(l => [key(l), l]));
  const out = [];
  (now || []).forEach(l => {
    const b = bm.get(key(l));
    if (!b) out.push({ itemId: l.itemId, secId: l.secId, sec: l.sec, desc: l.desc, was: null, now: l.amt });
    else if (r2(b.amt) !== r2(l.amt) || b.desc !== l.desc) out.push({ itemId: l.itemId, secId: l.secId, sec: l.sec, desc: l.desc, was: b.amt, now: l.amt, ...(b.desc !== l.desc ? { wasDesc: b.desc } : {}) });
  });
  (base || []).forEach(l => { if (!nm.has(key(l))) out.push({ itemId: l.itemId, secId: l.secId, sec: l.sec, desc: l.desc, was: l.amt, now: null }); });
  // grouped by section (in the order they first come), so a removed line is listed with the rest of its section
  const order = [...new Set(out.map(c => c.secId || c.sec))];
  return order.flatMap(s => out.filter(c => (c.secId || c.sec) === s));
}
export const changeTotal = changes => (changes || []).reduce((s, c) => r2(s + (c.now || 0) - (c.was || 0)), 0);

// Applies a change order's changes to a set of lines.
export function applyChanges(lines, changes) {
  const out = (lines || []).map(l => ({ ...l }));
  (changes || []).forEach(c => {
    const i = out.findIndex(l => key(l) === key(c));
    if (c.now == null) { if (i >= 0) out.splice(i, 1); return; }
    const l = { itemId: c.itemId, secId: c.secId, sec: c.sec, desc: c.desc, amt: c.now };
    if (i >= 0) out[i] = l; else out.push(l);
  });
  return out;
}

const time = d => Date.parse(d.created_at) || 0;
// Each issued document's standing: 'approved'; 'replaced' (approved, but a later estimate was approved instead);
// 'sent' (no approval recorded).
export function approvedState(docs, approvals) {
  const ok = new Map((approvals || []).map(a => [a.estimateId, a]));
  const asc = (docs || []).slice().sort((a, b) => time(a) - time(b));
  let bi = -1;
  asc.forEach((d, i) => { if (d.kind !== 'change' && ok.has(d.id)) bi = i; });
  const status = {};
  asc.forEach((d, i) => { status[d.id] = !ok.has(d.id) ? 'sent' : i < bi ? 'replaced' : 'approved'; });
  if (bi < 0) return { status, base: null };
  const base = asc[bi], parts = [base];
  let lines = (base.lines || []).map(l => ({ ...l }));
  asc.slice(bi + 1).forEach(d => { if (d.kind === 'change' && ok.has(d.id)) { lines = applyChanges(lines, d.changes); parts.push(d); } });
  return { status, base: { lines, total: sum(lines), ccy: base.ccy, parts, estimate: base, approval: ok.get(base.id) } };
}

// Purchase orders against what is approved and what the budget is now (POs in another currency are left out of
// the sums and counted in `other`).
export function poCover(pos, ccy, approved, now) {
  const live = (pos || []).filter(p => p.ccy === ccy);
  const total = live.reduce((s, p) => r2(s + (Number(p.amount) || 0)), 0);
  return { total, count: live.length, other: (pos || []).length - live.length,
    shortOfApproved: approved != null ? r2(approved - total) : null, shortOfNow: r2(now - total) };
}

export const docLabel = d => d ? `${d.kind === 'change' ? 'Change order' : 'Estimate'} ${d.label}` : '';
export const partsLabel = base => base ? base.parts.map(d => d.label).join(' + ') : '';
